import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Mail, Sparkles, ArrowRight, Globe, Wand2, Copy, ClipboardPaste,
  CheckCircle2, Zap, Users, BarChart3, ChevronLeft, Palette, Image as ImageIcon,
  FileText, MessageSquareQuote, XCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public product detail page for Simply Smarter Email.
// Lives at /simply-smarter-email — accessible without login, before signup.
// Reuses the existing GettingStartedPage so we don't duplicate content.

export default function SimplySmarterEmailPage() {
  const navigate = useNavigate();
  const [founderSpots, setFounderSpots] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/billing/founder-spots-remaining`)
      .then(r => setFounderSpots(r.data))
      .catch(() => { /* fail silent — counter just hides */ });
  }, []);

  const handleStart = () => navigate("/login");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ssemail-product-page">
      {/* Top brand nav — consistent with /suite */}
      <nav className="sticky top-0 z-50 bg-[#003858] text-white shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/suite" className="flex items-center gap-3" data-testid="ssemail-nav-logo">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-10 h-10 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold tracking-wide" style={{fontFamily: 'Manrope'}}>ANNE FLINN ADVERTISING</span>
              <span className="text-xs font-medium text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/suite" className="hidden sm:inline-flex items-center text-sm text-white/70 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> All Tools
            </Link>
            <Button
              data-testid="ssemail-nav-signin"
              variant="ghost"
              onClick={handleStart}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              Sign In
            </Button>
            <Button
              data-testid="ssemail-nav-cta"
              onClick={handleStart}
              className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-semibold"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-10 lg:py-14 text-center">
          <Badge className="bg-[#B7EB1B] text-[#0a1e3a] font-semibold px-3 py-1.5 mb-5">
            <Mail className="w-3 h-3 mr-1.5 inline" /> Available Now
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4" style={{fontFamily: 'Manrope'}}>
            Simply Smarter <span className="text-[#B7EB1B]">Email</span>
          </h1>
          <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            Turn any webpage into a beautiful email newsletter in about 60 seconds.
          </p>
        </div>
      </section>

      {/* See it work — real webpage → real email (moved UP so new users understand immediately) */}
      <section id="see-it-work" className="relative bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] text-white py-12 lg:py-16 overflow-hidden" data-testid="ssemail-see-it-work">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-widest text-[#B7EB1B] font-semibold mb-4">Super Easy to Use</p>
            <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mb-4">
              {[
                { n: 1, label: "Find Webpage" },
                { n: 2, label: "Grab URL" },
                { n: 3, label: "Edit Pre-Built Email" },
              ].map(({ n, label }, i, arr) => (
                <React.Fragment key={n}>
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full pl-2 pr-4 py-1.5">
                    <span className="w-7 h-7 rounded-full bg-[#B7EB1B] text-[#0a1e3a] flex items-center justify-center text-sm font-extrabold">
                      {n}
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
                      {label}
                    </span>
                  </div>
                  {i < arr.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-white/40 hidden sm:inline" />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="text-white/70 max-w-xl mx-auto">
              Here's an actual tourism page transformed into a ready-to-send newsletter — no template fussing, no HTML.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {/* LEFT: Find webpage */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#0d9488]/40 hover:bg-white group/step">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#0a1e3a] text-[#B7EB1B] flex items-center justify-center text-sm font-extrabold group-hover/step:ring-4 group-hover/step:ring-[#B7EB1B]/30 transition-all">1</span>
                Find Webpage
              </div>
              <p className="text-sm text-slate-600 mb-3">
                A tourism page about Sparta &amp; Alleghany County — paragraphs of text, landscape photos, clear sections. The kind of page you already have on your own website.
              </p>
              <a
                href="https://www.blueridgeparkway.org/communities/sparta-alleghany-county/"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg overflow-hidden border border-slate-200 shadow-sm hover:shadow-md hover:border-[#0d9488]/40 transition-all group"
                data-testid="ssemail-example-webpage-preview"
              >
                <img
                  src="/brand/example-webpage.png"
                  alt="Blue Ridge Parkway — Sparta & Alleghany County tourism page"
                  className="w-full h-auto block group-hover:opacity-95 transition-opacity"
                  loading="lazy"
                />
                <div className="flex items-center justify-between bg-white px-3 py-2 border-t border-slate-100 text-[11px]">
                  <span className="flex items-center gap-1.5 text-slate-500 font-mono truncate">
                    <Globe className="w-3 h-3 flex-shrink-0" />
                    blueridgeparkway.org/…/sparta-alleghany-county
                  </span>
                  <span className="text-[#0d9488] font-semibold whitespace-nowrap ml-2 group-hover:underline">Visit ↗</span>
                </div>
              </a>
            </div>

            {/* MIDDLE: Grab URL — the magic middle step */}
            <div
              className="relative rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#0d9488]/40 hover:bg-white group/step overflow-hidden"
              data-testid="ssemail-step-grab-url"
            >
              <div className="relative">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-[#0a1e3a] text-[#B7EB1B] flex items-center justify-center text-sm font-extrabold group-hover/step:ring-4 group-hover/step:ring-[#B7EB1B]/30 transition-all">2</span>
                  Grab URL
                </div>

                {/* Copy from browser */}
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copy from your browser
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 pl-2.5 pr-1 py-1.5 shadow-sm">
                    <Globe className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-[11px] text-slate-700 truncate font-mono flex-1">blueridgeparkway.org/…</span>
                    <span className="text-[9px] uppercase font-bold text-[#0d9488] bg-[#0d9488]/10 px-1.5 py-0.5 rounded transition-transform group-hover/step:scale-110">⌘C</span>
                  </div>
                </div>

                {/* Paste into app */}
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                    <ClipboardPaste className="w-3 h-3" /> Paste into Simply Smarter
                  </div>
                  <div className="flex items-center gap-2 bg-white border-2 border-dashed border-[#0d9488]/50 rounded-lg pl-2.5 pr-2 py-1.5 group-hover/step:border-[#0d9488] group-hover/step:bg-[#0d9488]/5 transition-all">
                    <ClipboardPaste className="w-3.5 h-3.5 text-[#0d9488] flex-shrink-0" />
                    <span className="text-[11px] text-slate-600 truncate font-mono flex-1">Paste URL here…</span>
                  </div>
                </div>

                {/* AI drafts arrow */}
                <div className="flex items-center justify-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#0a1e3a] flex items-center justify-center shadow-lg group-hover/step:scale-110 transition-transform">
                    <ArrowRight className="w-5 h-5 text-[#B7EB1B]" />
                  </div>
                  <div className="text-[10px] font-semibold text-[#0d9488] uppercase tracking-widest">
                    <Wand2 className="w-3 h-3 inline mr-0.5" /> AI drafts
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-2 text-center italic">Takes about 60 seconds</p>
              </div>
            </div>

            {/* RIGHT: Edit Pre-Built Email */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-[#0d9488]/40 hover:bg-white group/step">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#0a1e3a] text-[#B7EB1B] flex items-center justify-center text-sm font-extrabold group-hover/step:ring-4 group-hover/step:ring-[#B7EB1B]/30 transition-all">3</span>
                Edit Pre-Built Email
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_7a1f6cd5-42c6-490a-a613-e41c55c1c770/artifacts/m9kpo3ib_email%20preview.png"
                alt="Example generated email — Alleghany County newsletter"
                className="w-full rounded-lg shadow-lg border border-slate-200"
                data-testid="ssemail-example-preview"
              />
              <div className="flex flex-wrap gap-1.5 mt-3 justify-start">
                {["Try 13 beautiful themes", "Easily tweak colors", "Quickly swap in your photos", "Manually edit any pre-written text"].map(t => (
                  <span key={t} className="px-2.5 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded-full">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-white/60 mt-6 italic">
            You then tweak colors, swap in your own photos, and edit any text before you send.
          </p>
        </div>
      </section>

      {/* What's different — pain point grid (right after the 3-step visual demo) */}
      <section className="relative bg-gradient-to-br from-slate-50 via-[#0d9488]/10 to-[#B7EB1B]/10 py-14 lg:py-20 border-b border-slate-200/60 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#0d9488]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-[#B7EB1B]/15 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-widest text-[#0d9488] font-bold mb-3">What's different</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
              Built for the overwhelmed.
            </h2>
            <p className="text-slate-700 max-w-2xl mx-auto text-lg">
              Mailchimp has 200 features you'll never use. Simply Smarter Email has the <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0a1e3a] text-[#B7EB1B] text-base font-extrabold mx-1 align-middle">6</span> you actually need.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Zap, title: "60-second setup", desc: "No DNS, SMTP, or settings-menu spiral." },
              { icon: Wand2, title: "AI writes the first draft", desc: "Subject line, headlines, copy — in your voice." },
              { icon: Palette, title: "Easily make it yours", desc: "Tweak colors, swap photos, edit any text." },
              { icon: Sparkles, title: "13 beautiful themes", desc: "Industry-specific designs made by a real agency." },
              { icon: Users, title: "Contacts, made simple", desc: "Upload a CSV once. Reuse it forever." },
              { icon: BarChart3, title: "Opens & clicks tracked", desc: "See what's working — no spreadsheets required." },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="group relative rounded-2xl bg-white/80 backdrop-blur border border-white shadow-sm p-5 flex items-start gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:bg-white hover:border-[#0d9488]/30"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0d9488] to-[#003858] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-[#B7EB1B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 text-base mb-1 leading-tight" style={{fontFamily: 'Manrope'}}>
                    {title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-snug">{desc}</p>
                </div>
                <CheckCircle2 className="absolute top-3 right-3 w-4 h-4 text-[#0d9488]/30 group-hover:text-[#0d9488] transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* You're in control — Customization spotlight */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-16 lg:py-20 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          {/* Top: text + icon features in a 2-column layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-14">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#0d9488] font-semibold mb-3">The part nobody talks about</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-5" style={{fontFamily: 'Manrope'}}>
                AI gives you a head start. <br/>
                <span className="text-[#0d9488]">You make it yours.</span>
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                The AI draft is just the beginning. The magic is that <strong>YOU</strong> get to easily
                adjust everything — without needing design skills, HTML, or a phone call to your "tech person."
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-[#0a1e3a] text-white rounded-full px-4 py-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#B7EB1B]" />
                Most users customize their newsletter in 5 minutes or less
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-5 lg:mt-12">
              {[
                { icon: Palette, label: "Colors", desc: "Pick your brand colors. Watch everything re-skin instantly." },
                { icon: ImageIcon, label: "Your photos", desc: "Swap in your own images with one click. Use what looks like YOU." },
                { icon: Wand2, label: "Edit any text", desc: "Don't love a headline? Click and rewrite. The AI draft is a starting point, not a cage." },
                { icon: Sparkles, label: "Theme & layout", desc: "Switch between 13 industry-specific themes and 3 layouts with one click." },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="flex items-start gap-3 group">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0d9488] to-[#003858] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                    <Icon className="w-4 h-4 text-[#B7EB1B]" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm" style={{fontFamily: 'Manrope'}}>{label}</div>
                    <div className="text-xs text-slate-600 leading-snug">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: 3 horizontal screenshots with titles */}
          <div className="relative">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              {[
                {
                  src: "/brand/customize-cards.png",
                  alt: "Simply Smarter Email — labeled customize dropdowns for logo, header, layout, theme, and more",
                  title: "Easy Edit Drop Down Tools",
                  testid: "ssemail-shot-customize-cards",
                },
                {
                  src: "/brand/live-preview.png",
                  alt: "Simply Smarter Email — live preview showing the email being edited in real time",
                  title: "Immediate Design Updates & Previews",
                  testid: "ssemail-shot-live-preview",
                },
                {
                  src: "/brand/dropdown-open.png",
                  alt: "Simply Smarter Email — expanded theme picker with visual style options",
                  title: "Quickly Choose Colors and Layouts",
                  testid: "ssemail-shot-dropdown-open",
                },
              ].map(({ src, alt, title, testid }) => (
                <figure
                  key={testid}
                  className="flex flex-col group"
                  data-testid={testid}
                >
                  <h3 className="text-xs sm:text-sm uppercase tracking-widest text-[#0a1e3a] font-extrabold mb-3 text-center min-h-[2.5rem] flex items-center justify-center" style={{fontFamily: 'Manrope'}}>
                    {title}
                  </h3>
                  <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-900/5 bg-white border border-slate-200 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-2xl group-hover:border-[#0d9488]/40">
                    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
                  </div>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What pages work best — qualifier moved up-funnel so visitors self-select before signing up */}
      <section className="bg-white py-16 lg:py-20 border-t border-slate-100" data-testid="ssemail-page-fit">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <Badge className="bg-[#0d9488]/10 text-[#0d9488] border-[#0d9488]/20 mb-3 px-3 py-1">Before you sign up</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Manrope'}}>
              What pages work best with this tool?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The tool reads any URL you paste and turns it into an email. It works beautifully on most standard websites. A few page types it can't handle yet, and we want you to know up front.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Works great */}
            <div className="rounded-2xl border border-[#0d9488]/30 bg-gradient-to-br from-[#0d9488]/5 to-white p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-[#0d9488]/15 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#0d9488]" />
                </div>
                <h3 className="font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Works great</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d9488] flex-shrink-0 mt-0.5" />
                  <span>Blog posts &amp; articles with real paragraphs of text</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d9488] flex-shrink-0 mt-0.5" />
                  <span>Webpages built on WordPress, Squarespace, Wix, Webflow</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d9488] flex-shrink-0 mt-0.5" />
                  <span>Pages with clear headlines, body copy, and photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d9488] flex-shrink-0 mt-0.5" />
                  <span>Event pages, product pages, announcement posts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#0d9488] flex-shrink-0 mt-0.5" />
                  <span>More text and images on the page = better email out</span>
                </li>
              </ul>
            </div>

            {/* Doesn't work yet */}
            <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-amber-700" />
                </div>
                <h3 className="font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Not designed for</h3>
              </div>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Heavily interactive JavaScript apps (single-page apps)</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Sites behind a login or paywall</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Image-only pages with little or no body text</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>PDF documents or downloads (not real web pages)</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span>Pages that block automated readers (some news &amp; e-commerce)</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-8 max-w-2xl mx-auto">
            Not sure if your site will work? Start your 14-day free trial — no card required — and see it live with your own URL.
          </p>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="bg-gradient-to-br from-[#0a1e3a] via-[#003858] to-[#0d9488] py-16 lg:py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <Badge className="bg-white/10 text-white border-white/20 mb-4 px-3 py-1">
            <Sparkles className="w-3 h-3 mr-1.5 inline text-[#B7EB1B]" /> Simple pricing
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{fontFamily: 'Manrope'}}>
            $79/month. <span className="text-[#B7EB1B]">No limits.</span>
          </h2>
          <p className="text-white/80 text-lg mb-2 max-w-xl mx-auto">
            Unlimited campaigns. Unlimited contacts. Unlimited sends. Cancel anytime.
          </p>
          <p className="text-white/60 text-sm mb-8">
            14-day free trial · no credit card required
          </p>

          <div className="bg-white/5 backdrop-blur rounded-3xl p-6 border border-white/10 mb-8 max-w-md mx-auto">
            <p className="text-xs uppercase tracking-widest text-[#B7EB1B] font-semibold mb-2" data-testid="ssemail-founder-label">
              {founderSpots
                ? founderSpots.remaining === 0
                  ? "Founding Member · All spots claimed"
                  : <>Founding Member · <span className="text-white">{founderSpots.remaining}</span> of {founderSpots.cap} spots left</>
                : "Founding Member · First 100 customers"}
            </p>
            <div className="text-4xl font-extrabold mb-1" style={{fontFamily: 'Manrope'}}>$49<span className="text-lg text-white/60">/month</span></div>
            <p className="text-sm text-white/60">Locked in for life of subscription</p>
            {founderSpots && founderSpots.remaining > 0 && founderSpots.remaining <= 20 && (
              <p className="text-xs text-[#B7EB1B] font-semibold mt-3" data-testid="ssemail-founder-urgency">
                Almost gone — claim yours before it's $79/mo.
              </p>
            )}
          </div>

          <Button
            data-testid="ssemail-pricing-cta"
            size="lg"
            onClick={handleStart}
            className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-bold text-base px-10 h-14"
          >
            Start Your Free Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-xs text-white/40 mt-4">No card needed. We'll ask after the 14 days.</p>
        </div>
      </section>

      {/* Anne tie-back */}
      <section className="bg-white py-14 border-y border-slate-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center gap-6">
          <img
            src="/brand/anne-headshot.jpg"
            alt="Anne Flinn"
            className="w-20 h-20 rounded-full object-cover border-4 border-[#B7EB1B]/30 flex-shrink-0"
          />
          <div>
            <p className="text-slate-800 font-medium leading-relaxed">
              "Every small business needs to email their customers. Three decades in advertising taught me that. So I built a tool that does that one thing really well — and gets out of your way. My tools do less. On purpose."
            </p>
            <p className="text-sm text-slate-500 mt-2">
              — Anne Flinn, founder · <Link to="/suite" className="text-[#0d9488] hover:underline">See all tools →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Sister tools cross-link */}
      <section className="bg-slate-50 py-14 border-b border-slate-100" data-testid="ssemail-sister-tools">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-8">
            <Badge className="bg-[#0d9488]/10 text-[#0d9488] border-[#0d9488]/20 mb-3 px-3 py-1">
              <Sparkles className="w-3 h-3 mr-1.5 inline" /> Better together
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>
              Sister tools from the Simply Smarter suite
            </h3>
            <p className="text-slate-600 max-w-xl mx-auto">
              Each tool stands on its own. Together, they make small-business marketing dramatically simpler.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Scorecard cross-link */}
            <a
              href="https://brand-report-r-1773343747.emergent.host/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ssemail-cross-scorecard"
              className="group flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#B7EB1B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0a1e3a] to-[#003858] flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-6 h-6 text-[#B7EB1B]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#0d9488]">Free · No signup</span>
                </div>
                <div className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
                  Simply Smarter Scorecard
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  Before you send your first email, run a 2-minute brand audit. See how you show up across Google, social, and maps.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-[#0d9488] group-hover:text-[#0a1e3a] transition-colors">
                  Run Free Audit <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </a>

            {/* Customer Questionnaire cross-link */}
            <a
              href="https://consumer-check-1.preview.emergentagent.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ssemail-cross-questionnaire"
              className="group flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#B7EB1B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#006080] to-[#0d9488] flex items-center justify-center flex-shrink-0">
                <MessageSquareQuote className="w-6 h-6 text-[#B7EB1B]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#0d9488]">Free · No signup</span>
                </div>
                <div className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
                  Simply Smarter Customer Questionnaire
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  Where do your customers actually spend their media time? A 5-minute survey that tells you exactly where to buy ads.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-[#0d9488] group-hover:text-[#0a1e3a] transition-colors">
                  Open Dashboard <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </a>

            {/* Blog cross-link */}
            <a
              href="https://blog-factory-6.preview.emergentagent.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ssemail-cross-blog"
              className="group flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200 hover:border-[#B7EB1B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#119AB3] to-[#003858] flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#B7EB1B]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#0d9488]">$49/mo · Pairs perfectly</span>
                </div>
                <div className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>
                  Simply Smarter Blog Posts
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  Need more content to email? AI writes SEO-friendly blog posts in your voice. Each one doubles as a newsletter.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-[#0d9488] group-hover:text-[#0a1e3a] transition-colors">
                  Explore Blog Posts <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003858] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-8 h-8" />
            <div className="text-sm text-white/60">
              © {new Date().getFullYear()} Anne Flinn Advertising, LLC · <span className="text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <Link to="/suite" className="hover:text-white/80">All Tools</Link>
            <span>·</span>
            <span>Email infrastructure powered by Swiftlett</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
